import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Building2,
  UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const FALLBACK = { candidates_on_radar: 3288, new_this_week: 229, companies_active: 30 };

function useCountUp(target: number, active: boolean) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let frame = 0;
    const total = 45;
    const id = setInterval(() => {
      frame++;
      const p = 1 - Math.pow(1 - frame / total, 3);
      setValue(Math.round(target * p));
      if (frame >= total) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [target, active]);
  return value;
}

const SWEEP_SECONDS = 6;

/** Punten op de radar: hoek (graden, 0 = boven) + afstand vanaf het midden (0-1). */
const BLIPS: { angle: number; radius: number; Icon: typeof UserRound; accent?: boolean }[] = [
  { angle: 0, radius: 0.92, Icon: Building2 },
  { angle: 52, radius: 0.72, Icon: Building2, accent: true },
  { angle: 96, radius: 0.95, Icon: Building2 },
  { angle: 142, radius: 0.55, Icon: Building2 },
  { angle: 196, radius: 0.88, Icon: Building2 },
  { angle: 236, radius: 0.5, Icon: Building2 },
  { angle: 272, radius: 0.9, Icon: Building2, accent: true },
  { angle: 318, radius: 0.58, Icon: Building2 },
];

export const RadarSection = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(FALLBACK);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase as any).rpc("get_talentradar_live_stats");
      if (!cancelled && !error && data?.[0]) setStats(data[0]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const count = useCountUp(Number(stats.candidates_on_radar) || 0, visible);

  return (
    <section className="relative gradient-cyan py-16 md:py-24">
      <div ref={ref} className="container mx-auto px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Kop */}
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-4xl md:text-6xl font-heading font-black mb-3">
              Zet jezelf op de radar!
            </h2>
            <p className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto">
              En laat werkgevers bij jou solliciteren. 
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Datakaart links */}
            <div className="order-2 lg:order-1">
              <div className="radar-dashboard rounded-[24px] px-8 py-7 md:px-10 md:py-8 relative overflow-hidden">
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(115deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 32%, transparent 60%)",
                  }}
                />

                <div className="relative">
                  {/* Live badge linksboven */}
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                    </span>
                    <span className="text-[11px] font-bold tracking-wide uppercase opacity-80">
                      Live op de TalentRadar
                    </span>
                  </div>

                  {/* Groot getal */}
                  <div className="flex items-start gap-2 sm:gap-3 flex-nowrap mt-4">
                    <span className="text-5xl sm:text-6xl md:text-7xl font-heading font-black leading-none tabular-nums">
                      {count.toLocaleString("nl-NL")}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-emerald-400/15 text-emerald-300 px-2.5 py-1 text-xs sm:text-sm font-bold tabular-nums whitespace-nowrap leading-none self-start mt-1 sm:mt-1.5">
                      +{Number(stats.new_this_week).toLocaleString("nl-NL")} deze week
                    </span>
                  </div>
                  <p className="text-base md:text-lg font-semibold opacity-70 mt-2">
                    mensen staan nu op de radar
                  </p>

                  <div className="h-px bg-white/10 my-5" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      variant="cta"
                      size="lg"
                      className="group w-full h-14 text-base shadow-lg"
                      onClick={() => navigate("/signup/kandidaat?src=radar")}
                    >
                      Zet mij op de radar
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full h-14 text-base bg-transparent border-white/25 text-current hover:bg-white/10 hover:text-current"
                      onClick={() => navigate("/start/bedrijven")}
                    >
                      Ik zoek talent
                    </Button>
                  </div>
                </div>
              </div>

              <p className="text-xs md:text-sm text-foreground/60 mt-3 px-1">
                 &nbsp;Gratis · 1 minuut setup · Direct zichtbaar
              </p>
            </div>

            {/* Radar rechts */}
            <div className="order-1 lg:order-2">
              <div className="relative mx-auto w-full max-w-[520px]">
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[115%] aspect-square rounded-full opacity-70 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, hsl(var(--primary) / 0.45) 0%, hsl(var(--primary) / 0.12) 55%, transparent 75%)",
                    filter: "blur(40px)",
                  }}
                />

                <div className="relative aspect-square">
                  <div
                    className="absolute inset-0 rounded-full border border-white/50 backdrop-blur-[2px]"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.9) 0%, rgba(200,230,255,0.4) 70%, rgba(255,255,255,0) 100%)",
                      boxShadow:
                        "0 0 80px rgba(100,180,255,0.35), 0 30px 60px rgba(0,0,0,0.12)",
                    }}
                  />
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 18%, rgba(255,255,255,0.85), transparent 52%)",
                    }}
                  />
                  <div className="absolute inset-[6%] rounded-full border border-primary/20" />
                  <div className="absolute inset-[22%] rounded-full border border-primary/25" />
                  <div className="absolute inset-[38%] rounded-full border border-primary/30" />
                  <div className="absolute inset-[54%] rounded-full border border-primary/35" />

                  <div
                    className="radar-sweep absolute inset-0 rounded-full overflow-hidden"
                    style={{
                      background:
                        "conic-gradient(from 0deg, hsl(var(--primary) / 0.5), hsl(var(--primary) / 0.1) 30%, transparent 58%)",
                    }}
                  >
                    <div
                      className="absolute left-1/2 top-0 h-1/2 w-[3px] -translate-x-1/2 rounded-full"
                      style={{
                        background:
                          "linear-gradient(to bottom, hsl(var(--primary) / 0) 0%, hsl(var(--primary) / 0.95) 100%)",
                        boxShadow: "0 0 12px hsl(var(--primary) / 0.6)",
                      }}
                    />
                  </div>

                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-background shadow-xl border border-border flex items-center justify-center">
                      <UserRound className="w-9 h-9 md:w-11 md:h-11 text-primary" />
                    </div>
                  </div>

                  {BLIPS.map(({ angle, radius, Icon, accent }, i) => {
                    const rad = ((angle - 90) * Math.PI) / 180;
                    const left = 50 + Math.cos(rad) * radius * 45;
                    const top = 50 + Math.sin(rad) * radius * 45;
                    return (
                      <div
                        key={i}
                        className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
                        style={{ left: `${left}%`, top: `${top}%` }}
                      >
                        <div
                          className={`radar-blip w-10 h-10 md:w-12 md:h-12 rounded-full shadow-lg border flex items-center justify-center ${
                            accent
                              ? "bg-[hsl(var(--yellow))] border-[hsl(var(--yellow))] text-[hsl(var(--yellow-foreground))]"
                              : "bg-background border-border text-primary"
                          }`}
                          style={{ animationDelay: `-${SWEEP_SECONDS * (1 - angle / 360)}s` }}
                        >
                          <Icon className="w-4 h-4 md:w-5 md:h-5" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="absolute left-1/2 bottom-[4%] -translate-x-1/2 w-[70%] h-8 rounded-[50%] bg-foreground/10 blur-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
